import { IContextualMenuItem } from "@fluentui/react";
import * as React from "react";

// NOTE: no Element.closest / classList / NodeList.forEach below. package.json
// still targets IE 11 (legacy Office webviews), which lacks them, and core-js
// does not polyfill DOM methods; an optional call there would silently switch a
// guard off.

const F2_KEY_CODE = 113;

// key OR keyCode, like the grid's Delete handler: legacy webviews can report a
// missing or "Unidentified" key. The keyCode fallback is only consulted when
// there is no usable key name, so a named key can never be mistaken for F2.
const isF2 = (event: KeyboardEvent): boolean =>
    event.key === "F2" || ((!event.key || event.key === "Unidentified") && event.keyCode === F2_KEY_CODE);

// Anything the user can type into: F2 there belongs to the editor. The attribute
// check covers engines (and jsdom) where isContentEditable is unreliable.
const isEditableElement = (element: HTMLElement): boolean => {
    const tagName = element.tagName?.toUpperCase();
    if (tagName === "INPUT" || tagName === "TEXTAREA" || element.isContentEditable === true) {
        return true;
    }

    const contentEditable = element.getAttribute?.("contenteditable");
    return contentEditable === "" || contentEditable === "true";
};

// Fluent portals every ContextualMenu, Dropdown list, Callout, Dialog and Panel
// into its OWN root carrying this class, as siblings under <body> even when they
// are nested in React. Nothing in this app opts out of layering.
const fluentLayerClassPattern = /(^|\s)ms-Layer(\s|$)/;
const isFluentLayer = (element: HTMLElement): boolean =>
    fluentLayerClassPattern.test(element.getAttribute?.("class") ?? "");

const findFluentLayer = (start: Node | null): HTMLElement | null => {
    for (let node: Node | null = start; node; node = node.parentNode) {
        if (node.nodeType === 1 && isFluentLayer(node as HTMLElement)) {
            return node as HTMLElement;
        }
    }
    return null;
};

// True when the key was pressed somewhere that owns the keyboard: a text field,
// or an open menu / dropdown / callout (they take focus into their layer).
// The grid's OWN layer does not count: CMFAdvancedSearchPanel hosts the search
// grid inside a Fluent Panel, and F2 has to work there too.
const isKeyboardOwnedAt = (target: Node, ownLayer: HTMLElement | null): boolean => {
    for (let node: Node | null = target; node; node = node.parentNode) {
        if (node.nodeType !== 1) {
            continue;
        }
        if (isEditableElement(node as HTMLElement)) {
            return true;
        }
        if (node !== ownLayer && isFluentLayer(node as HTMLElement)) {
            return true;
        }
    }
    return false;
};

// Every Dialog and Panel in this app is blocking, and Fluent renders an
// ms-Overlay for each blocking one. Tooltips and callouts never do, so this is
// "a dialog or panel is open" without being tripped by a hover tooltip. Needed
// on top of the focus check because a click on the dimmed area can leave focus
// on <body> while the dialog is still up. The overlay of the panel the grid
// itself lives in is not "on top of" the grid, so it does not count; one that
// sits in no layer at all always does.
const isCoveredByDialogOrPanel = (ownLayer: HTMLElement | null): boolean => {
    const overlays = document.querySelectorAll(".ms-Overlay");
    for (let index = 0; index < overlays.length; index++) {
        const overlayLayer = findFluentLayer(overlays[index]);
        if (overlayLayer === null || overlayLayer !== ownLayer) {
            return true;
        }
    }
    return false;
};

// Attached and not under display:none. Computed style rather than offsetParent /
// getClientRects so it is also meaningful under jsdom, which has no layout. Only
// ever runs on an actual F2 press.
const isOnScreen = (element: HTMLElement): boolean => {
    let node: Node | null = element;
    while (node && node.nodeType === 1) {
        if (window.getComputedStyle(node as HTMLElement).display === "none") {
            return false;
        }
        node = node.parentNode;
    }
    // A detached subtree runs out of parents before reaching the document.
    return node === document;
};

/**
 * F2 = "click the grid's Rename command".
 *
 * Pass the rename menu item the grid already built (null when rename is not
 * offered for the current selection) and put the returned ref on the element
 * wrapping the grid. Eligibility is deliberately not re-derived here, so the
 * shortcut can never be more permissive than the menu.
 *
 * F2 follows the VISIBLE SELECTION, not DOM focus. The grids keep the selection
 * when empty space is clicked (selectionPreservedOnEmptyClick), which drops
 * focus to <body> while the row still looks selected; marquee selection and a
 * panel closing back to the command bar do the same. A handler scoped to focus
 * inside the grid went dead in all of those. So this listens on the document and
 * instead stands down when something else owns the keyboard, or when its own
 * grid is not on screen (ContentManager keeps the main grid mounted under
 * display:none while advanced search shows; a page-wide listener without this
 * check would open a rename dialog for the hidden grid).
 *
 * Cost: a couple of comparisons per keystroke. Every DOM read happens only on an
 * actual F2 press, and the listener is subscribed once per mount because the
 * current item is read through a ref.
 */
export function useRenameShortcut<T extends HTMLElement = HTMLDivElement>(renameItem: IContextualMenuItem | null | undefined): React.RefObject<T> {
    const wrapperRef = React.useRef<T>(null);

    const renameItemRef = React.useRef(renameItem);
    renameItemRef.current = renameItem;

    React.useEffect(() => {
        const onRenameShortcutKeyDown = (event: KeyboardEvent): void => {
            if (!isF2(event) || event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) {
                return;
            }

            // Held-down F2, or a descendant already claimed it (an inline editor, say).
            // React 16 dispatches from the document too, and subscribed first.
            if (event.repeat || event.defaultPrevented) {
                return;
            }

            const item = renameItemRef.current;
            if (!item || item.disabled || !item.onClick) {
                return;
            }

            const wrapper = wrapperRef.current;
            if (!wrapper || !isOnScreen(wrapper)) {
                return;
            }

            const ownLayer = findFluentLayer(wrapper);
            const target = event.target;
            if (target instanceof Node && isKeyboardOwnedAt(target, ownLayer)) {
                return;
            }

            if (isCoveredByDialogOrPanel(ownLayer)) {
                return;
            }

            event.preventDefault();
            // Should two hooked grids ever be visible together, only one dialog opens.
            event.stopImmediatePropagation();
            item.onClick(undefined, item);
        };

        document.addEventListener("keydown", onRenameShortcutKeyDown);
        return () => document.removeEventListener("keydown", onRenameShortcutKeyDown);
    }, []);

    return wrapperRef;
}
